import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Global attention badge count — polled from /notifications/attention-count every 60s.
 * Also refreshes on window focus (user switches back to the tab).
 * Consumed by RouteTitle to prefix document.title with "(N) ...".
 *
 * Role-aware content on the backend:
 *  - admin:   pending withdrawals + open disputes
 *  - brand:   submitted deliveries pending approval + new applications
 *  - creator: pending invitations + revision-requested contracts
 */
const AttentionContext = createContext({ count: 0, hint: null, items: [], refresh: () => {} });

export const useAttentionBadge = () => useContext(AttentionContext);

const POLL_INTERVAL_MS = 60_000;

export function AttentionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ count: 0, hint: null, items: [] });
  const inFlight = useRef(false);

  const fetchCount = useCallback(async () => {
    if (inFlight.current) return;
    if (!user || user === false || user === null) {
      setState({ count: 0, hint: null, items: [] });
      return;
    }
    inFlight.current = true;
    try {
      const r = await api.get("/notifications/attention-count");
      setState({
        count: r.data.count || 0,
        hint: r.data.hint || null,
        items: r.data.items || [],
      });
    } catch (_) {
      // Silently ignore — endpoint may fail transiently. Keep last state.
    } finally {
      inFlight.current = false;
    }
  }, [user]);

  // Poll interval + initial fetch
  useEffect(() => {
    fetchCount();
    const iv = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchCount]);

  // Refresh on tab focus (user comes back to browser tab)
  useEffect(() => {
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchCount]);

  return (
    <AttentionContext.Provider value={{ ...state, refresh: fetchCount }}>
      {children}
    </AttentionContext.Provider>
  );
}
